
import { mapActions, mapGetters } from 'vuex'
import CreateModelCompose from '@/components/ekrm/CreateModelCompose.vue'
import NgtorchRunChip from '@/components/NgtorchRunChip.vue'
import { isComposerEkrm } from '@/utils/ekrmOrigin.js'
import { getModelReportQuality } from '@/utils/modelReport.js'
import { copyTextToClipboard } from '@/utils/clipboard.js'

// Turns Firestore Timestamps and plain Dates into milliseconds; a serverTimestamp the snapshot has not
// resolved yet is null, and sorts last.
function toMillis (date) {
  if (date && typeof date.toMillis === 'function') return date.toMillis()
  if (date instanceof Date) return date.getTime()
  return 0
}

export default {
  data () {
    return {
      currentTab: 0,
      composeDialog: false,
      search: '',
      ekrmLoading: true,
      customLoading: true,
      deleteDialog: false,
      deleteItem: null,
      snack: false,
      snackColor: '',
      snackText: ''
    }
  },
  methods: {
    ...mapActions('manager', [
      'loadEkrmModels',
      'loadCustomModels',
      'deleteEkrmModel',
      'deleteCustomModel'
    ]),
    ...mapActions('clients', [
      'loadNewClient',
      'setCurrentClient'
    ]),
    marketName (market) {
      if (!market) return ''
      const match = this.countries.filter(country => country.id === market)[0]
      return match ? match.name : market
    },
    formatDateRelative (timestamp) {
      const diff = Math.max(0, Math.floor((Date.now() - timestamp) / 1000))
      if (diff < 60) return this.$t('views.modelComposer.relNow')
      const unit = (key, n) => this.$tc(`views.modelComposer.${key}`, n, { n: n })
      const minutes = Math.floor(diff / 60)
      if (minutes < 60) return unit('relMinutes', minutes)
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return unit('relHours', hours)
      const days = Math.floor(hours / 24)
      if (days < 30) return unit('relDays', days)
      const months = Math.floor(days / 30)
      if (months < 12) return unit('relMonths', months)
      return unit('relYears', Math.floor(months / 12))
    },
    formatDateCreated (timestamp) {
      return new Date(timestamp).toLocaleString(this.$i18n.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    showSnack (color, text) {
      this.snack = true
      this.snackColor = color
      this.snackText = text
    },
    copyId (id) {
      copyTextToClipboard(id).then(copied => {
        if (copied) {
          this.showSnack('success', this.$t('views.modelComposer.idCopied'))
        }
      })
    },
    openReport (item) {
      if (!item.client) return
      const id = item.client.id
      const open = () => {
        this.setCurrentClient(id)
        this.$router.push({ name: 'modelReport' })
      }
      if (this.clients.some(client => client.id === id)) {
        open()
      } else {
        this.loadNewClient({ id: id }).then(open)
      }
    },
    viewJSON (item) {
      console.log(JSON.stringify(item.client || item.ekrm))
    },
    closeDeleteDialog () {
      this.deleteDialog = false
      this.deleteItem = null
    },
    // The model and the EKRM it was built from go together, to the trash and back: the EKRM is
    // offered nowhere else, so one left behind could never be seen or removed. An EKRM still being
    // built has no model to restore and is deleted for good.
    confirmDelete () {
      const item = this.deleteItem
      if (!item) return
      const deleted = item.client
        ? this.deleteCustomModel({ client: item.client.id, source: 'modelComposer', includeEkrm: true })
        : this.deleteEkrmModel({ ekrm_id: item.ekrm.id })
      deleted.then(() => {
        this.showSnack('success', this.$t('views.modelComposer.snackDeleted'))
      }).catch(error => {
        console.error(error)
        this.showSnack('error', this.$t('views.modelComposer.snackErrorDeleting'))
      })
      this.closeDeleteDialog()
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('manager', [
      'ekrmModels',
      'customModels'
    ]),
    ...mapGetters('settings', [
      'countries'
    ]),
    ...mapGetters('clients', [
      'clients'
    ]),
    loading () {
      return this.ekrmLoading || this.customLoading
    },
    // One row per EKRM the composer made: the custom model ekrmBuilt created from it once there is
    // one, or the EKRM itself while ngtorch is still building it.
    composedRows () {
      const clientsByEkrm = {}
      this.customModels.forEach(client => {
        if (client.ekrm_id) {
          clientsByEkrm[client.ekrm_id] = client
        }
      })
      return this.ekrmModels.filter(ekrm => isComposerEkrm(ekrm)).map(ekrm => {
        const client = clientsByEkrm[ekrm.id] || null
        const reference = ekrm.definition && ekrm.definition.reference ? ekrm.definition.reference : {}
        const market = client && client.custom_model ? client.custom_model.country : reference.market
        return {
          id: client ? client.id : ekrm.id,
          ekrm: ekrm,
          client: client,
          name: client ? client.name : reference.category,
          countryName: this.marketName(market),
          quality: client ? getModelReportQuality(client) : null,
          created: toMillis(client ? client.date_created : ekrm.date_created),
          createdBy: client ? client.created_by_name : ekrm.created_by,
          status: client ? 'ready' : 'building'
        }
      })
    },
    headers () {
      return [
        { text: this.$t('views.modelComposer.headerName'), value: 'name' },
        { text: this.$t('views.modelComposer.headerCountry'), value: 'countryName' },
        { text: this.$t('views.modelComposer.headerQuality'), value: 'quality' },
        { text: this.$t('views.modelComposer.headerCreated'), value: 'created', filterable: false },
        { text: this.$t('views.modelComposer.headerCreatedBy'), value: 'createdBy' },
        { text: this.$t('views.modelComposer.headerStatus'), value: 'status', sortable: false, filterable: false },
        { text: '', value: 'actions', sortable: false, filterable: false, width: '100px' }
      ]
    }
  },
  components: {
    CreateModelCompose,
    NgtorchRunChip
  },
  created () {
    this.loadEkrmModels().then(() => {
      this.ekrmLoading = false
    })
    this.loadCustomModels().then(() => {
      this.customLoading = false
    })
  }
}
